import { wp } from '@/lib/api'

import page from '@/models/page'
import post from '@/models/post'
import attachment from '@/models/attachment'
import taxonomy, { type RawTaxonomy } from '@/models/taxonomy'
import cpt, { type RawCpt, type CptModel } from '@/models/cpt'
import { getWithEtag, type EtagResult } from '@/services/etag'

type PageModel = ReturnType<typeof page>
type PostModel = ReturnType<typeof post>
type AttachmentModel = ReturnType<typeof attachment>
type TaxonomyModel = ReturnType<typeof taxonomy>

const API_ENDPOINTS = {
  campaigns: 'campaigns',
  faqs: 'faqs',
  locations: 'locations',
  subscriptions: 'subscriptions',
  notifications: 'notifications',
  reviews: 'reviews',
  trainers: 'trainers',
  taxonomies: [
    'term_faq',
    'term_locations',
    'term_subscriptions',
  ],
} as const

type TaxonomyEndpoint = (typeof API_ENDPOINTS.taxonomies)[number]
export type ContentResourceKey =
  | 'pages'
  | 'posts'
  | 'attachments'
  | 'taxonomies'
  | 'membership'
  | 'trainers'
  | 'gyms'
  | 'faqs'
export type TaxonomyEtags = Partial<Record<TaxonomyEndpoint, string>>
export type ContentEtag = string | TaxonomyEtags
export type ContentEtags = Partial<Record<ContentResourceKey, ContentEtag>>
export type ContentResource<T> = {
  data: T
  etag: ContentEtag | null
  notModified: boolean
}

function stringEtag(etag: ContentEtag | undefined): string | undefined {
  return typeof etag === 'string' ? etag : undefined
}

function taxonomyEtags(etag: ContentEtag | undefined): TaxonomyEtags {
  return typeof etag === 'object' && etag !== null ? etag : {}
}

function mapResult<Raw, Model>(
  result: EtagResult<Raw[]>,
  map: (raw: Raw) => Model,
): ContentResource<Model[]> {
  return {
    data: result.notModified ? [] : result.data.map(map),
    etag: result.etag,
    notModified: result.notModified,
  }
}

async function fetchList<Raw, Model>(
  path: string,
  map: (raw: Raw) => Model,
  etag?: ContentEtag,
): Promise<ContentResource<Model[]>> {
  const result = await getWithEtag<Raw[]>(
    wp,
    path,
    {},
    stringEtag(etag),
    [],
  )

  return mapResult(result, map)
}

async function fetchCpt(
  restBase: string,
  etag?: ContentEtag,
): Promise<ContentResource<CptModel[]>> {
  return fetchList<RawCpt, CptModel>(restBase, cpt, etag)
}

async function fetchTaxonomies(
  etag?: ContentEtag,
  currentData: TaxonomyModel[] = [],
): Promise<ContentResource<TaxonomyModel[]>> {
  const currentEtags = taxonomyEtags(etag)
  const results = await Promise.allSettled(
    API_ENDPOINTS.taxonomies.map(async (endpoint) => ({
      endpoint,
      result: await getWithEtag<RawTaxonomy[]>(
        wp,
        endpoint,
        {},
        currentEtags[endpoint],
        [],
      ),
    })),
  )

  const nextEtags: TaxonomyEtags = { ...currentEtags }
  let hasModifiedResource = false
  const data = results.flatMap((settled, index) => {
    const endpoint = API_ENDPOINTS.taxonomies[index]

    if (settled.status === 'rejected') {
      return currentData.filter((item) => item.taxonomy === endpoint)
    }

    const { result } = settled.value
    if (result.etag) {
      nextEtags[endpoint] = result.etag
    } else {
      delete nextEtags[endpoint]
    }

    if (result.notModified) {
      return currentData.filter((item) => item.taxonomy === endpoint)
    }

    hasModifiedResource = true
    return result.data.map(taxonomy)
  })

  return {
    data,
    etag: Object.keys(nextEtags).length > 0 ? nextEtags : null,
    notModified: !hasModifiedResource,
  }
}

export const ContentService = {
  pages(etag?: ContentEtag): Promise<ContentResource<PageModel[]>> {
    return fetchList('pages', page, etag)
  },
  posts(etag?: ContentEtag): Promise<ContentResource<PostModel[]>> {
    return fetchList('posts', post, etag)
  },
  attachments(etag?: ContentEtag): Promise<ContentResource<AttachmentModel[]>> {
    return fetchList('media', attachment, etag)
  },
  membership(etag?: ContentEtag): Promise<ContentResource<CptModel[]>> {
    return fetchCpt(API_ENDPOINTS.subscriptions, etag)
  },
  trainers(etag?: ContentEtag): Promise<ContentResource<CptModel[]>> {
    return fetchCpt(API_ENDPOINTS.trainers, etag)
  },
  gyms(etag?: ContentEtag): Promise<ContentResource<CptModel[]>> {
    return fetchCpt(API_ENDPOINTS.locations, etag)
  },
  faqs(etag?: ContentEtag): Promise<ContentResource<CptModel[]>> {
    return fetchCpt(API_ENDPOINTS.faqs, etag)
  },
  taxonomies(
    etag?: ContentEtag,
    currentData?: TaxonomyModel[],
  ): Promise<ContentResource<TaxonomyModel[]>> {
    return fetchTaxonomies(etag, currentData)
  },
}
